import { Link } from 'react-router-dom';
import { Award, ExternalLink, Cpu, Layers, Flame, Target, Compass, BookOpen, ChevronRight, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';
import { getBreadcrumbSchema } from '../data/schemas';

const BirdClefDetail = ({ item }) => {
  return (
    <div className="animate-fade-in-down max-w-4xl mx-auto space-y-10">
      <SEO
        title="BirdCLEF+ 2026: Bioacoustic Species Classification & State Space Models"
        description="Competitive bioacoustics Kaggle research achieving 0.92207 V5 score using Google Perch v2 embeddings, ProtoSSM State Space Models, and multi-taxon feature fusion."
        path="/ideas/birdclef-2026"
        keywords={['BirdCLEF 2026', 'Bioacoustics AI', 'Google Perch v2', 'ProtoSSM', 'State Space Models', 'Audio Classification', 'Kaggle Bioacoustics', 'Koneko', 'NotKoneko']}
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'ResearchProject',
              name: 'BirdCLEF+ 2026: Bioacoustic Species Classification',
              description: 'Classifying bioacoustic soundscapes with Google Perch v2 and State Space Models.',
              url: 'https://portfolio.notkoneko.space/ideas/birdclef-2026',
              author: {
                '@id': 'https://portfolio.notkoneko.space/#person'
              }
            },
            getBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Ideas & Research', url: '/ideas' },
              { name: 'BirdCLEF+ 2026', url: '/ideas/birdclef-2026' }
            ])
          ]
        }}
      />
      {/* Back Link */}
      <Link to="/ideas" className="inline-flex items-center gap-2 text-sm font-mono text-gray-400 hover:text-fuchsia-300 transition-colors group">
        <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Research Lab
      </Link>

      {/* Hero Banner */}
      <div className="bg-[#090714] p-6 sm:p-10 border border-purple-500/30 rounded-3xl shadow-2xl relative overflow-hidden space-y-6">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-600/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="flex flex-wrap items-center justify-between gap-3 relative z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono px-3.5 py-1 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded-full flex items-center gap-1.5">
              <Compass className="w-3.5 h-3.5 text-fuchsia-400" /> Bioacoustics &amp; Soundscape AI
            </span>
            <span className="text-xs font-mono px-3 py-1 bg-pink-950/60 text-pink-300 border border-pink-500/40 rounded-full">
              Kaggle Challenge
            </span>
          </div>
          <span className="text-xs font-mono text-gray-400 bg-[#0d0a1c] px-3 py-1 rounded-full border border-purple-500/20">
            March - June 2026
          </span>
        </div>

        <h1 className="text-3xl sm:text-5xl font-serif text-white tracking-wide leading-tight relative z-10">
          BirdCLEF+ 2026: Bioacoustic Species Classification
        </h1>

        <p className="text-base sm:text-lg text-gray-300 leading-relaxed relative z-10 font-serif italic">
          — A Kaggle competitive bioacoustics project classifying species from raw soundscapes using Google Perch v2 embeddings, ProtoSSM State Space Models, and multi-taxon feature fusion.
        </p>

        {/* Action Buttons to Kaggle */}
        <div className="pt-2 flex flex-wrap gap-4 relative z-10">
          <a
            href="https://www.kaggle.com/competitions/birdclef-2026"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 border border-purple-400/30 text-white text-xs font-mono font-bold rounded-xl transition-all shadow-lg shadow-purple-600/25 flex items-center gap-2.5 group"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current text-purple-200 shrink-0" aria-hidden="true">
              <path d="M18.825 23.859h-3.225l-5.719-7.712-2.156 2.062v5.65H4.8V.141h2.925v14.756l7.031-7.856h3.45l-6.169 6.881 6.788 10.037z" />
            </svg>
            <span>View Competition Page</span>
            <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <a
            href="https://www.kaggle.com/nottkoneko"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-3 bg-[#0d0a1c] hover:bg-purple-950/40 border border-purple-500/30 text-fuchsia-300 hover:text-white text-xs font-mono font-bold rounded-xl transition-all shadow-lg flex items-center gap-2.5 group"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current text-pink-400 shrink-0" aria-hidden="true">
              <path d="M18.825 23.859h-3.225l-5.719-7.712-2.156 2.062v5.65H4.8V.141h2.925v14.756l7.031-7.856h3.45l-6.169 6.881 6.788 10.037z" />
            </svg>
            <span>Kaggle Profile (NottKoneko)</span>
            <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>
      </div>

      {/* Performance Scorecard Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* 1. Evaluation Score */}
        <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 shadow-xl relative overflow-hidden group hover:border-fuchsia-500/40 transition-colors">
          <div className="flex items-center justify-between text-gray-400 mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-purple-400/90">[ 01 // SCORE ]</span>
            <Award className="w-5 h-5 text-fuchsia-400" />
          </div>
          <div className="text-3xl font-extrabold text-fuchsia-300 font-mono tracking-tight">0.92207</div>
          <p className="text-[11px] text-gray-400 mt-1 font-mono">Contest Spread: <span className="text-gray-300">0.31969</span> – <span className="text-gray-300">0.96574</span></p>
        </div>

        {/* 2. Best Iteration */}
        <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 shadow-xl relative overflow-hidden group hover:border-pink-500/40 transition-colors">
          <div className="flex items-center justify-between text-gray-400 mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-pink-400/90">[ 02 // ITERATION ]</span>
            <Flame className="w-5 h-5 text-pink-400" />
          </div>
          <div className="text-3xl font-extrabold text-pink-300 font-mono tracking-tight">0.92207 <span className="text-sm font-normal text-pink-400">V5</span></div>
          <p className="text-[11px] text-gray-400 mt-1 font-mono">Google Perch v2 + ProtoSSM</p>
        </div>

        {/* 3. Leaderboard Rank */}
        <div className="bg-[#090714] p-6 rounded-2xl border border-purple-500/20 shadow-xl relative overflow-hidden group hover:border-purple-400/40 transition-colors">
          <div className="flex items-center justify-between text-gray-400 mb-2">
            <span className="text-xs font-mono uppercase tracking-wider text-purple-300/90">[ 03 // RANK ]</span>
            <Target className="w-5 h-5 text-purple-300" />
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-purple-200 font-mono flex items-center gap-2 flex-wrap tracking-tight">
            <span>2151<span className="text-gray-500 font-normal text-xl">/4095</span></span>
            <span className="text-xs font-semibold px-2 py-0.5 bg-purple-950/80 text-fuchsia-300 rounded-full border border-purple-500/40">Top ~52.5%</span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1 font-mono">Global ranking placement</p>
        </div>
      </div>

      {/* Personal Journey & Reflections */}
      <section className="bg-[#090714] p-6 sm:p-10 border border-purple-500/20 rounded-3xl space-y-6 shadow-xl">
        <h2 className="text-2xl font-serif text-white flex items-center gap-3 border-b border-purple-500/20 pb-4">
          <BookOpen className="w-6 h-6 text-fuchsia-400" />
          <span>Personal Reflection &amp; Lessons Learned</span>
        </h2>

        <div className="space-y-4 text-gray-300 leading-relaxed font-sans text-sm sm:text-base">
          <div className="bg-[#0d0a1c] p-5 rounded-2xl border-l-4 border-l-fuchsia-500 border border-purple-500/20 space-y-2">
            <h3 className="text-base font-bold text-white font-serif">
              My First Kaggle Challenge
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              BirdCLEF+ 2026 was my first competitive machine learning challenge on Kaggle. Jumping straight into an audio bioacoustics competition, where models identify hundreds of bird species in noisy 60-second soundscapes, ended up being a much larger task than I expected.
            </p>
          </div>

          <div className="space-y-3 pt-2">
            <h4 className="text-base font-bold text-white font-serif">
              The Engineering Process: Tuning &amp; Researching
            </h4>
            <p>
              Rather than starting from scratch on a 100+ GB audio dataset, I adopted a standard competitive workflow: cloning a strong open-source baseline notebook and analyzing its pipeline to optimize predictive performance.
            </p>
            <p>
              My focus was on improving the validation AUC score, eventually reaching a top submission of <strong className="text-fuchsia-300 font-mono">0.92207 (V5)</strong> and placing <strong className="text-pink-300 font-mono">2151 / 4095 (Top ~52.5%)</strong> across a contest score spread of 0.31969 to 0.96574. The pipeline combined Google Perch v2 audio embeddings with State Space Models (ProtoSSM) and cross-attention mechanics, which required digging deep into the model math to make working edits.
            </p>
            <p>
              While post-processing probability tweaks and hyperparameter tuning gave clear score boosts, I realized there were custom architectural changes I couldn't fully implement yet without more foundation practice.
            </p>
          </div>

          <div className="bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20 space-y-3 pt-4">
            <h4 className="text-base font-bold text-white font-serif">
              Next Steps &amp; Future Roadmap
            </h4>
            <p className="text-sm text-gray-300 font-serif italic">
              — Working through this competition gave me a clear look at competitive ML workflows and highlighted the specific technical areas I want to focus on next.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-mono pt-1 text-gray-300">
              <li className="flex items-center gap-2 bg-[#090714] p-3 rounded-xl border border-purple-500/20">
                <CheckCircle2 className="w-4 h-4 text-fuchsia-400 shrink-0" />
                <span>Focus on smaller, focused Kaggle tabular &amp; NLP tasks</span>
              </li>
              <li className="flex items-center gap-2 bg-[#090714] p-3 rounded-xl border border-purple-500/20">
                <CheckCircle2 className="w-4 h-4 text-pink-400 shrink-0" />
                <span>Build pipelines end-to-end completely from scratch</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Deep Technical Architecture & Notebook Breakdown */}
      <article className="bg-[#090714] p-6 sm:p-10 border border-purple-500/20 rounded-3xl space-y-8 text-gray-300 leading-relaxed font-sans shadow-xl">
        <div className="border-b border-purple-500/20 pb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/50 border border-purple-500/30 text-purple-300 text-xs font-mono mb-2">
            <Cpu className="w-3.5 h-3.5 text-fuchsia-400" /> Technical Deep-Dive
          </div>
          <h2 className="text-2xl font-serif text-white">System Architecture &amp; Code Breakdown</h2>
          <p className="text-xs font-mono text-gray-400 mt-1">Analysis of birdclef-2026.ipynb (ProtoSSM v4 / Enhanced Ensemble Pipeline)</p>
        </div>

        {/* Architecture Diagram / Blocks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20 space-y-2">
            <div className="flex items-center gap-2 text-fuchsia-400 text-sm font-mono font-bold">
              <Layers className="w-4 h-4" /> [ 01 // PERCH V2 BACKBONE ]
            </div>
            <p className="text-xs text-gray-300 leading-relaxed">
              Extracts high-dimensional acoustic embeddings (`perch_v2_cpu/1`) across 5-second sliding windows (12 windows per 60-second soundscape recording). Maps species taxonomy directly to iNaturalist 2024 labels.
            </p>
          </div>

          <div className="bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20 space-y-2">
            <div className="flex items-center gap-2 text-pink-400 text-sm font-mono font-bold">
              <Cpu className="w-4 h-4" /> [ 02 // PROTOSSM MODELING ]
            </div>
            <p className="text-xs text-gray-300 leading-relaxed">
              Combines Mamba-inspired State Space Models (`d_model=128`, `d_state=16`, 2 SSM layers) with 4-head Multi-Head Cross-Attention and prototype vectors to model temporal context across sequential soundscape windows.
            </p>
          </div>

          <div className="bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20 space-y-2">
            <div className="flex items-center gap-2 text-purple-300 text-sm font-mono font-bold">
              <ChevronRight className="w-4 h-4" /> [ 03 // TAXON FUSION ]
            </div>
            <p className="text-xs text-gray-300 leading-relaxed">
              Differentiates between discrete event vocalizations (birds / *Aves*) and continuous background texture (*Amphibia* / *Insecta*). Applies calibrated temperature scaling (`aves=1.10`, `texture=0.95`).
            </p>
          </div>

          <div className="bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20 space-y-2">
            <div className="flex items-center gap-2 text-purple-200 text-sm font-mono font-bold">
              <Flame className="w-4 h-4" /> [ 04 // RESIDUAL BOOSTING ]
            </div>
            <p className="text-xs text-gray-300 leading-relaxed">
              Second-pass Residual SSM (`d_model=64`) corrects prediction residuals. Test-Time Augmentation (TTA temporal shifts `[-1, 0, 1]`) and probability power transform (`probs ** 0.85`) boost overall ROC AUC.
            </p>
          </div>
        </div>

        {/* Code Highlights Summary */}
        <div className="space-y-4 pt-2">
          <h3 className="text-lg font-serif font-bold text-white">Key Code Configurations &amp; Hyperparameters</h3>
          <div className="bg-[#06050b] p-5 rounded-2xl border border-purple-500/20 font-mono text-xs overflow-x-auto space-y-3 shadow-inner">
            <div className="text-gray-400">// Excerpt from CFG configuration matrix in birdclef-2026.ipynb</div>
            <div className="text-purple-300">
              <span className="text-fuchsia-400">"proto_ssm"</span>: &#123;<br />
              &nbsp;&nbsp;<span className="text-gray-300">"d_model"</span>: 128, <span className="text-gray-300">"d_state"</span>: 16, <span className="text-gray-300">"n_ssm_layers"</span>: 2,<br />
              &nbsp;&nbsp;<span className="text-gray-300">"use_cross_attn"</span>: true, <span className="text-gray-300">"cross_attn_heads"</span>: 4<br />
              &#125;,
            </div>
            <div className="text-pink-300">
              <span className="text-fuchsia-400">"power_transform"</span>: <span className="text-gray-300">probs = probs ** 0.85</span> <span className="text-gray-500">// Spreads probability distribution for optimal AUC ranking</span>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BirdClefDetail;
